<template>
	<Modal width="md" @requestUnmount="$emit('safeToUnmount')">
		<template #header>
			<div class="font-semibold text-lg mb-2">{{ $t('Add to classroom') }}</div>
		</template>

		<template #default="modalActions">
			<div class="px-2 py-4">
				<p class="mb-4">{{ addDescription }}</p>

				<div v-if="errorMessage" class="alert alert-error text-sm mb-4">
					{{ errorMessage }}
				</div>

				<select
					v-model="targetClassroomId"
					class="select select-bordered w-full mb-4"
				>
					<option :value="null" disabled>{{ $t('Select classroom') }}...</option>
					<option v-for="classroom in classrooms.filter(c => c.id !== currentClassroom.id)" :key="classroom.id" :value="classroom.id">
						{{ classroom.name }}
					</option>
				</select>

				<label class="flex items-start gap-3 cursor-pointer mb-4">
					<input type="checkbox" v-model="alsoRemoveFromCurrent" class="checkbox checkbox-sm mt-0.5" />
						<i18n-t keypath="And remove them from the classroom {name}" tag="span">
							<template #name>
								"<strong>{{ currentClassroom.name }}</strong>"
							</template>
						</i18n-t>
				</label>
			</div>

			<div class="modal-footer flex justify-end gap-2 mt-2 overflow-hidden">
				<button type="button" class="btn btn-ghost" @click="modalActions.hide">{{ $t('Cancel') }}</button>
				<button
					type="button"
					class="btn btn-primary"
					:class="{ 'btn-disabled': !targetClassroomId || processing }"
					@click="confirm(modalActions)"
				>
					<span v-if="processing" class="loading loading-spinner loading-sm"></span>
					{{ $t('Confirm') }}
				</button>
			</div>
		</template>
	</Modal>
</template>

<script lang="ts">
import Modal from "@/components/ui/Modal.vue";
import { mapStores } from "pinia";
import { usePupilsStore } from "@/stores/Pupils.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag";
import { Pupil } from "@/models/Pupil.model";
import { Classroom } from "@/models/Classroom.model";
import type { PropType } from 'vue';

export default {
	name: "PupilBulkAddModal",

	components: {
		Modal,
	},

	props: {
		selectedPupils: {
			type: Array as PropType<Pupil[]>,
			required: true,
		},
		classrooms: {
			type: Array as PropType<Classroom[]>,
			required: true,
		},
		currentClassroom: {
			type: Object as PropType<Classroom>,
			required: true,
		},
	},

	emits: ["safeToUnmount"],

	data() {
		return {
			targetClassroomId: null as string | null,
			alsoRemoveFromCurrent: false,
			processing: false,
			errorMessage: null as string | null,
		};
	},

	computed: {
		...mapStores(usePupilsStore),

		addDescription(): string {
			return this.$t('Add the {count} selected pupils to', { count: this.selectedPupils.length }) + '...';
		},
	},

	methods: {
		async confirm(modalActions: any) {
			if (!this.targetClassroomId || this.processing) return;

			const targetClassroom = this.classrooms.find((c: Classroom) => c.id === this.targetClassroomId);
			if (!targetClassroom) return;

			// Snapshot before awaiting — the store reload after bulkAddToClassroom resets
			// the parent's bulkSelectedPupilIds watcher, which would re-select excluded pupils.
			const pupils = [...this.selectedPupils];

			this.errorMessage = null;
			this.processing = true;
			try {
				await this.pupilsStore.bulkAddToClassroom(pupils, targetClassroom);
				if (this.alsoRemoveFromCurrent) {
					await this.pupilsStore.bulkRemoveFromClassroom(pupils, this.currentClassroom);
					this.$notify({ text: this.$t('The pupils have been successfully moved'), type: 'success' });
				} else {
					this.$notify({ text: this.$t('The pupils have been successfully added'), type: 'success' });
				}
				modalActions.hide();
			} catch (e: any) {
				this.errorMessage = e instanceof ApiErrorMessageBag
					? e.toString()
					: this.$t('An error occurred');
			} finally {
				this.processing = false;
			}
		},
	},
};
</script>
